import { createContext, useCallback, useEffect, useMemo, useReducer } from "react";
import moment from "moment-timezone";
import { reducer } from "./reducer";
import { initalState } from "./initialState";
import { useEventLayout } from "../../hooks/grid/useEventLayout";
import { useDateFilters } from "../../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useUser } from "../../../../../utils/hooks/user";
import { unassignedShiftsResource } from "../../helpers/data/unassignedShiftsResource";
import { mapOpenShiftRequest } from "../../helpers/data/mapOpenShiftRequest";
import { assignEventsToGroupedResources } from "../../helpers/data/getGroupedResources";
import type { ScheduleProviderProps, ScheduleState } from "../../types/ScheduleState";
import type { OpenShift } from "../../types/OpenShift";
import type { Event } from "../../types/Event";

export const ScheduleContext = createContext<ScheduleState>(initalState);

export const ScheduleProvider = ({ type, children }: ScheduleProviderProps) => {
    const user = useUser();
    const [filters] = useFilters();
    const userTimezone = moment.tz.guess();
    const [state, dispatch] = useReducer(reducer, initalState);

    const events: Event[] = useMemo(() => {
        const homeOpenShifts = (type === "home" && filters?.mode !== "month")
            ? (state.openShifts ?? [])
                .map((shift: OpenShift) => ({
                    ...mapOpenShiftRequest(shift, userTimezone),
                    resourceId: user?.id
                }))
            : [];
        // these skip the reducer, so put them in the user's grouped row here
        return [...assignEventsToGroupedResources(homeOpenShifts, state.allResources), ...state.events]
    }, [type, filters?.mode, state.openShifts, state.events, state.allResources, user?.id, userTimezone]);

    const { resourceLayouts, eventLayouts } = useEventLayout(events, state.resources, filters?.mode);
    const { resourceLayouts: unassignedResourceLayout, eventLayouts: unassignedEventLayouts } = useEventLayout(
        state.unassignedEvents,
        [unassignedShiftsResource],
        filters?.mode,
    );

    const clearSelectedEvents = useCallback(() => {
        dispatch({ type: "select-events", payload: [] });
        dispatch({ type: "select-unassigned-events", payload: [] });
    }, [dispatch]);

    const { mode, from, to } = useDateFilters();

    useEffect(() => {
        clearSelectedEvents();
    }, [from, to, mode, clearSelectedEvents]);

    const value: ScheduleState = useMemo(() => ({
        ...state,
        events,
        type,
        resourceLayouts,
        eventLayouts,
        unassignedResourceLayout,
        unassignedEventLayouts,
        dispatch,
        clearSelectedEvents
    }), [
        state,
        events,
        type,
        resourceLayouts,
        eventLayouts,
        unassignedResourceLayout,
        unassignedEventLayouts,
        dispatch,
        clearSelectedEvents
    ]);

    return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>;
};
